import { getSetting } from '@disy/workbook.lib/settings';

import { type RootStateOr, getStore } from 'cadenza/store/cadenza-store';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { getViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key.gen';
import { isLiveModeRunning } from 'cadenza/workbook/state/workbook-live-mode-selectors.state';
import { isLayerVisible } from 'cadenza/workbook/state/workbook-map-layer.state';
import {
  getAttributeById,
  getDataViewById,
  getDataViewIdsByViewId,
  getViewById,
  getViews,
  getWorksheetByViewId,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

/**
 * @return <code>false</code> if the setting 'TIMELINE_3D_ENABLED' is already set to <code>true</code>
 * by another view with different viewId in the same worksheet, <code>true</code> else.
 */
export const is3dAvailableForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return (
    !getWorksheetByViewId(state, viewId)?.views.some(
      (worksheetViewId) =>
        getSetting<boolean>(
          getViewById(state, worksheetViewId).settings, // We have it enabled (active)
          MapViewSettingsKey.toPersistenceKey('TIMELINE_3D_ENABLED'),
          false,
        ) && viewId !== worksheetViewId, // And it is used by a view with a different id (other view)
    ) && viewHasTimelineData(state, viewId)
  ); // And the view has some timeline-data in it
};

export const isTimeline2dAvailableForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const mapHasTimelineData = viewHasTimelineData(getStore().getState(), viewId);
  const liveModeRunning = isLiveModeRunning(state);
  return mapHasTimelineData && !liveModeRunning;
};

/**
 * Returns true if the given view has a valid configuration with timeline data and thus could
 * be used for the timeline player or is suitable for 3d mode.
 *
 * @param state - Cadenza state
 * @param viewId - id of the data view to check
 * @return true if the given data view has a valid configuration with timeline data
 */
export function viewHasTimelineData(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const view = getViewById(state, viewId);
  return (
    view &&
    getDataViewIdsByViewId(state, viewId).some((dataViewId) =>
      isDataViewVisibleAndHasTimelineData(state, viewId, dataViewId),
    )
  );
}

export function getDataViewIdsWithTimelineData(state: RootStateOr<WorkbookState>) {
  return getViews(state).flatMap(({ id }) => getDataViewIdsWithTimelineDataByView(state, id));
}

export function getDataViewIdsWithTimelineDataByView(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const view = getViewById(state, viewId);
  return view ?
      getDataViewIdsByViewId(state, viewId).filter((dataViewId: DataViewId) =>
        isDataViewVisibleAndHasTimelineData(state, viewId, dataViewId),
      )
    : [];
}

export function shouldTimelinesBeSortedByRenderOrder(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const timelineDataViewIds = getDataViewIdsWithTimelineDataByView(state, viewId);
  return timelineDataViewIds.some((timelineDataViewId) => {
    const dataView = getDataViewById(state, timelineDataViewId);
    return !!dataView?.attributes.DRAWING_ORDER?.[0];
  });
}

function isDataViewVisibleAndHasTimelineData(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  dataViewId: DataViewId,
) {
  return dataViewHasTimelineData(state, dataViewId) && isLayerVisible(state, viewId, dataViewId);
}

/**
 * Returns true if the given data view has a valid configuration with timeline data and thus could
 * be used for the timeline player.
 *
 * @param state - Cadenza state
 * @param dataViewId - id of the data view to check
 * @return true if the given data view has a valid configuration with timeline data
 */
export function dataViewHasTimelineData(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const dataView = getDataViewById(state, dataViewId);
  const sortingAttributes = dataView.attributes['SEQUENCE_ORDER'];
  return (
    getViewType(dataView.visualizationKey) === 'MAP' &&
    // Has sorting bundle and sorting attributes
    !!sortingAttributes?.length &&
    (dataView.status === 'VALID' || dataView.status === 'INVALID_FROM_NOT_QUERY_RELEVANT_BUNDLE') &&
    allObligatoryBundlesConfigured(dataView) && // Bundle configuration is valid
    // Although we could have the timeline visualization for numeric attributes, we check in addition for the
    // DATA type because the timeline player and 3D view should be enabled only for DATE sorting, not numbers.
    getAttributeById(state, sortingAttributes[0]).type === 'DATE'
  );
}

function allObligatoryBundlesConfigured(dataView: DataViewDto) {
  return Object.values(dataView.attributeBundleDefinitions).every((bundleDef) => {
    const minAttributeNum = bundleDef.minNumberOfAttributes ?? 0;
    const actualAttributeNum = dataView.attributes[bundleDef.attributeBundleName]!.length;
    return actualAttributeNum >= minAttributeNum;
  });
}
